import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import type { ChipProps } from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import {
  type Ad,
  type AdPlacement,
  AdPlacementSchema,
  type AdStatus,
  AdStatusSchema,
  PROFESSIONAL_TITLES,
} from '@q4udoc/types';
import {
  AutocompleteArrayInput,
  BooleanField,
  BooleanInput,
  Button,
  Create,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  ImageField,
  ImageInput,
  List,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { Link } from 'react-router-dom';
import AdPreviewPanel from '../components/AdPreviewPanel';
import { StatusChip } from '../components/ChoiceChips';
import { RejectButton } from '../components/RejectButton';
import { ScrollableDatagrid } from '../components/ScrollableDatagrid';
import { useResourceAction } from '../hooks/useResourceAction';
import { useSpecialtyOptions } from '../hooks/useSpecialtyOptions';

// Targeting picker options come from the canonical career-stage vocab in @q4udoc/types, so they
// stay in lockstep with the stored user.professionalTitle values the matcher compares against
// (the old hand-maintained list offered 'PA Student'/'Pharmacist' etc. that could never match).
const professionalTitleOptions = PROFESSIONAL_TITLES.map((title) => ({ id: title, name: title }));

// Targeting is two axes only — medical specialty and professional title (career stage). The old
// PGY / interests / locations dimensions were dropped; see AdTargetingSchema in @q4udoc/types.
const TargetingInputs = () => {
  const specialtyOptions = useSpecialtyOptions();

  return (
    <>
      <h4>Targeting</h4>
      <AutocompleteArrayInput source="targeting.specialties" choices={specialtyOptions} />
      <AutocompleteArrayInput
        source="targeting.professionalTitles"
        choices={professionalTitleOptions}
        label="Professional Titles"
      />
    </>
  );
};

// Ids come from the shared enums so a new status or placement cannot be missed here; the
// human labels stay local.
const STATUS_LABELS: Record<AdStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
};
const statusChoices = AdStatusSchema.options.map((id) => ({ id, name: STATUS_LABELS[id] }));
const statusColors: Record<AdStatus, ChipProps['color']> = {
  draft: 'default',
  pending_review: 'warning',
  approved: 'success',
  rejected: 'error',
};

const PLACEMENT_LABELS: Record<AdPlacement, string> = {
  quiz_interstitial: 'Quiz Interstitial',
  feed_banner: 'Feed Banner',
  feed_inline: 'Feed Inline',
};
const placementChoices = AdPlacementSchema.options.map((id) => ({
  id,
  name: PLACEMENT_LABELS[id],
}));

export const AdList = () => (
  <List
    filters={[
      <ReferenceInput key="campaignId" source="campaignId" reference="campaigns">
        <SelectInput optionText="name" label="Campaign" />
      </ReferenceInput>,
      <SelectInput key="status" source="status" choices={statusChoices} label="Review Status" />,
      <SelectInput key="placement" source="placement" choices={placementChoices} />,
      <BooleanInput key="enabled" source="enabled" label="Active Only" />,
    ]}
    empty={
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Ads Found
        </Typography>
        <Button
          component={Link}
          to="/ads/create"
          variant="contained"
          label="Create Ad"
          startIcon={<AddIcon />}
        />
      </Box>
    }
  >
    <ScrollableDatagrid>
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <ReferenceField source="campaignId" reference="campaigns">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="title" />
        <ImageField
          source="imageUrl"
          sx={{ '& img': { maxWidth: 50, maxHeight: 50, objectFit: 'contain' } }}
        />
        <TextField source="placement" />
        <StatusChip choices={statusChoices} colors={statusColors} />
        <BooleanField source="enabled" />
      </Datagrid>
    </ScrollableDatagrid>
  </List>
);

const ApproveAdButton = () => {
  const record = useRecordContext();
  const approve = useResourceAction('ads', 'approve', {
    success: 'Ad approved successfully',
    error: 'Error approving ad',
  });
  if (record?.status !== 'pending_review') return null;
  return (
    <Button label="Approve" onClick={() => approve()} startIcon={<CheckIcon />} color="success" />
  );
};

const AdShowActions = () => (
  <TopToolbar>
    <EditButton />
    <ApproveAdButton />
    <RejectButton
      resource="ads"
      pendingStatus="pending_review"
      title="Reject Ad"
      helperText="Explain why this ad is being rejected..."
      success="Ad rejected"
      error="Error rejecting ad"
    />
  </TopToolbar>
);

const AdPreview = () => {
  const record = useRecordContext<Ad>();
  if (!record) return null;
  return (
    <Box sx={{ my: 2 }}>
      <AdPreviewPanel data={record} />
    </Box>
  );
};

export const AdShow = () => (
  <Show actions={<AdShowActions />}>
    <SimpleShowLayout>
      <StatusChip choices={statusChoices} colors={statusColors} />
      <AdPreview />
      <ReferenceField source="campaignId" reference="campaigns">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="title" />
      <TextField source="text" label="Ad Text" />
      <TextField source="ctaText" label="CTA Text" />
      <TextField source="targetUrl" label="Target URL" />
      <TextField source="placement" />
      <TextField source="priority" />
      <BooleanField source="enabled" />
      <TextField source="rejectionReason" label="Rejection Reason" />
      <TextField source="reviewedBy" label="Reviewed By" />
      <DateField source="reviewedAt" label="Reviewed At" emptyText="—" />
      <DateField source="createdAt" />
    </SimpleShowLayout>
  </Show>
);

const AdFormFields = () => (
  <>
    <ReferenceInput source="campaignId" reference="campaigns">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <TextInput source="title" />

    <ImageInput
      source="imageUrl"
      label="Image"
      accept="image/*"
      format={(value) => {
        if (!value) return undefined;
        if (typeof value === 'string') return { src: value };
        return value;
      }}
    >
      <ImageField source="src" title="title" />
    </ImageInput>
    <TextInput source="text" label="Ad Text" multiline fullWidth />
    <TextInput source="ctaText" label="CTA Button Text" fullWidth />
    <TextInput source="targetUrl" label="Target URL" fullWidth />

    <SelectInput source="placement" choices={placementChoices} />
    {/* Review status is server-controlled: new ads enter pending_review, and from there it
        flows only through the Approve / Reject actions (auto-reopening when creative changes),
        so it is not a form field. */}
    <NumberInput
      source="priority"
      min={1}
      max={10}
      helperText="Higher priority ads are shown first (1-10)"
    />
    <NumberInput
      source="weight"
      min={1}
      helperText="Relative weight for random selection among ads with same priority"
    />
    <BooleanInput source="enabled" />
    <TargetingInputs />
  </>
);

export const AdEdit = () => (
  <Edit>
    <SimpleForm>
      <AdFormFields />
    </SimpleForm>
  </Edit>
);

// The defaults live on the Create form, not the inputs: on Edit an input default is merged
// under the record and would write weight=1 into legacy ads that never stored one.
export const AdCreate = () => (
  <Create>
    <SimpleForm defaultValues={{ priority: 5, weight: 1, enabled: true }}>
      <AdFormFields />
    </SimpleForm>
  </Create>
);
